import { useSaudiListDateRange } from '../../products/saudi/hooks/useSaudiListDateRange';
﻿import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { MapPin, Search, Plus, Edit, Trash2, RefreshCcw, CheckCircle, AlertTriangle, X, ChevronDown, ChevronUp, Upload, Download, FileSpreadsheet, FileOutput, Filter, Eye, LayoutGrid, Layers, Users, Building, List, LayoutList, Calendar, FileText, Warehouse, Construction, Home, Briefcase, Car, Coffee, XCircle } from 'lucide-react';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import { useColumnHeaderFilters } from '../../shared/hooks/useColumnHeaderFilters';
import { applyColumnFilters, textColumnRule, exactColumnRule } from '../../shared/utils/columnHeaderFilters';
import localApiAdapter from '../../core/services/localApiAdapter';
import QawaemErpCrudModal, { QawaemErpViewModal } from '../../products/saudi/components/QawaemErpCrudModal';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import QawaemClickableStatusBadge from '../../products/saudi/components/QawaemClickableStatusBadge';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import useModuleConfigTheme from '../../shared/hooks/useModuleConfigTheme';
import {
  usePointageModalBodyLock,
  getPointageModalFormClasses,
  buildPointageColumnVisibilityState,
  buildPointageQawaemColumnDefs,
  isPointageColumnVisible,
  createPointageHandleToggleColumn,
  pointageTableColSpan,
  buildPointageBulkDeleteFilterAction,
  POINTAGE_ACTIONS_TH_CLASS,
  PointageCrudNotificationToast,
  useAppNotificationToast,
  notifyBulkDeleted,
  notifyCreated,
  notifyDeleted,
  notifyError,
  notifyUpdated,
  sortRecordsNewestFirst,
  buildPointageFormTabs,
  pointageFormTabVisible,
} from './pointageListPageUtils';
import {
  QawaemTableSelectAllHeader,
  QawaemTableSelectRowCell,
  useQawaemFilteredRowSelection,
} from '../../products/saudi/components/QawaemTableSelection';
import PointageFormModalShell, { POINTAGE_FORM_GRID_CLASS } from './components/PointageFormModalShell';
import RequiredLabelText from '../../shared/components/custom/RequiredLabelText';
import CustomSelect from '../../shared/components/custom/CustomSelect';
import CustomNumberInput from '../../shared/components/custom/CustomNumberInput';
import CustomDatePicker from '../../shared/components/custom/CustomDatePicker';
import { getErpOverviewGridAttrs } from '../../shared/utils/erpOverviewGridAttrs';
import { formatPointageDate, getSaudiWorkCityOptions, getSaudiWorkCityLabel, ZONE_TYPE_VALUES, normalizeZoneType } from './pointageHelpers';

const ZonesTravailPage = () => {
  const { t } = useTranslation();
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();
  const p = (key, opts) => t(`modules.pointage.pages.zonesTravailPage.${key}`, opts);

  const getStatusLabel = (status) => {
    if (status === 'Actif') return p('status_active');
    if (status === 'Inactif') return p('status_inactive');
    if (status === 'En Construction') return p('status_under_construction');
    return status || p('em_dash');
  };

  const cityOptions = getSaudiWorkCityOptions();

  const getZoneTypeLabel = (value) => {
    const normalized = normalizeZoneType(value);
    const map = {
      [ZONE_TYPE_VALUES.office]: p('type_office'),
      [ZONE_TYPE_VALUES.factory]: p('type_factory'),
      [ZONE_TYPE_VALUES.warehouse]: p('type_warehouse'),
      [ZONE_TYPE_VALUES.site]: p('type_site'),
      [ZONE_TYPE_VALUES.branch]: p('type_agency'),
    };
    return map[normalized] || value || p('em_dash');
  };

  const zoneTypeOptions = [
    { value: ZONE_TYPE_VALUES.office, label: p('type_office'), icon: <Briefcase size={14} className="text-blue-400" />, isDefault: true },
    { value: ZONE_TYPE_VALUES.factory, label: p('type_factory'), icon: <Building size={14} className="text-green-400" />, isDefault: true },
    { value: ZONE_TYPE_VALUES.warehouse, label: p('type_warehouse'), icon: <Warehouse size={14} className="text-purple-400" />, isDefault: true },
    { value: ZONE_TYPE_VALUES.site, label: p('type_site'), icon: <Construction size={14} className="text-orange-400" />, isDefault: true },
    { value: ZONE_TYPE_VALUES.branch, label: p('type_agency'), icon: <Home size={14} className="text-pink-400" />, isDefault: true },
  ];

  const getCityDisplay = (value) => getSaudiWorkCityLabel(value);

  const [searchTerm, setSearchTerm] = useState('');
  const [zones, setZones] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [viewMode, setViewMode] = useState('table');
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingRecord, setViewingRecord] = useState(null);

  const cfgTheme = useModuleConfigTheme();
  const modalForm = getPointageModalFormClasses(cfgTheme.light);
  usePointageModalBodyLock(showModal, showViewModal);
  
  const [formTab, setFormTab] = useState('general');

  const ZONE_FORM_FIELD_COUNT = 14;

  const zoneFormTabs = useMemo(() => buildPointageFormTabs({
    two: [
      { id: 'general', label: p('section_zone_info'), icon: MapPin, iconClass: 'text-emerald-400' },
      { id: 'details', label: p('section_manager'), icon: Briefcase, iconClass: 'text-orange-400' },
    ],
  }, ZONE_FORM_FIELD_COUNT), [p]);
  
  const [formData, setFormData] = useState({ 
    zone_code: '',
    zone_name: '',
    zone_type: '',
    address: '',
    city: '',
    postal_code: '',
    capacity_employees: '',
    current_employees: '',
    manager_name: '',
    has_parking: false,
    has_cafeteria: false,
    status: '',
    opening_date: '',
    notes: ''
  });
  
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const { dateRange, setDateRange } = useSaudiListDateRange();
  const [isDateRangeOpen, setIsDateRangeOpen] = useState(false);
  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [mainViewMode, setMainViewMode] = useState('all_zones');
  const [quickFilter, setQuickFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isStatusFilterOpen, setIsStatusFilterOpen] = useState(false);
  const [activeDistributionFilter, setActiveDistributionFilter] = useState(null);
  
  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);
  const statusFilterRef = useRef(null);
  const dateRangeRef = useRef(null);
  
  const { filters: colFilters, setFilter: setColFilter, editingKey, setEditingKey } = useColumnHeaderFilters();

  useEffect(() => { loadZones(); }, []);
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) setShowImportDropdown(false);
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) setShowExportDropdown(false);
      if (statusFilterRef.current && !statusFilterRef.current.contains(event.target)) setIsStatusFilterOpen(false);
      if (dateRangeRef.current && !dateRangeRef.current.contains(event.target)) setIsDateRangeOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadZones = async () => {
    try {
      const response = await localApiAdapter.get('/pointage/zones-travail');
      const rows = response?.data || response || [];
      setZones(sortRecordsNewestFirst(Array.isArray(rows) ? rows : []));
    } catch (error) {
      console.error('Error loading zones:', error);
      setZones([]);
    }
  };

  const stats = {
    total: zones.length,
    active: zones.filter(z => z.status === 'Actif').length,
    bureaux: zones.filter(z => normalizeZoneType(z.zone_type) === ZONE_TYPE_VALUES.office).length,
    usines: zones.filter(z => normalizeZoneType(z.zone_type) === ZONE_TYPE_VALUES.factory).length,
    totalEmployees: zones.reduce((sum, z) => sum + (parseInt(z.current_employees) || 0), 0)
  };

  const mainTabs = [
    { id: 'all_zones', name: p('tab_general'), icon: LayoutGrid },
    { id: 'par_type', name: p('tab_by_type'), icon: Building },
    { id: 'par_ville', name: p('tab_by_city'), icon: MapPin }
  ];

  const quickFilterButtons = [
    { id: 'all', name: p('filter_all'), icon: Layers },
    { id: ZONE_TYPE_VALUES.office, name: p('type_office'), icon: Briefcase },
    { id: ZONE_TYPE_VALUES.factory, name: p('type_factory'), icon: Building },
    { id: ZONE_TYPE_VALUES.warehouse, name: p('type_warehouse'), icon: Warehouse },
    { id: ZONE_TYPE_VALUES.site, name: p('type_site'), icon: Construction }
  ];

  const getTypeDistribution = () => {
    const bureau = zones.filter(z => normalizeZoneType(z.zone_type) === ZONE_TYPE_VALUES.office).length;
    const usine = zones.filter(z => normalizeZoneType(z.zone_type) === ZONE_TYPE_VALUES.factory).length;
    const entrepot = zones.filter(z => normalizeZoneType(z.zone_type) === ZONE_TYPE_VALUES.warehouse).length;
    const chantier = zones.filter(z => normalizeZoneType(z.zone_type) === ZONE_TYPE_VALUES.site).length;
    const agence = zones.filter(z => normalizeZoneType(z.zone_type) === ZONE_TYPE_VALUES.branch).length;
    
    return [
      { filterKey: ZONE_TYPE_VALUES.office, name: p('type_office'), count: bureau, color: "bg-gradient-to-r from-blue-500 to-blue-600" },
      { filterKey: ZONE_TYPE_VALUES.factory, name: p('type_factory'), count: usine, color: "bg-gradient-to-r from-green-500 to-green-600" },
      { filterKey: ZONE_TYPE_VALUES.warehouse, name: p('type_warehouse'), count: entrepot, color: "bg-gradient-to-r from-purple-500 to-purple-600" },
      { filterKey: ZONE_TYPE_VALUES.site, name: p('type_site'), count: chantier, color: "bg-gradient-to-r from-orange-500 to-orange-600" },
      { filterKey: ZONE_TYPE_VALUES.branch, name: p('type_agency'), count: agence, color: "bg-gradient-to-r from-pink-500 to-pink-600" }
    ];
  };

  const getCityDistribution = () => {
    const cityCount = {};
    zones.forEach(z => {
      const cityKey = z.city || 'unspecified';
      cityCount[cityKey] = (cityCount[cityKey] || 0) + 1;
    });
    
    const sortedCities = Object.entries(cityCount)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5);
    
    const colors = [
      "bg-gradient-to-r from-blue-500 to-blue-600",
      "bg-gradient-to-r from-green-500 to-green-600",
      "bg-gradient-to-r from-purple-500 to-purple-600",
      "bg-gradient-to-r from-orange-500 to-orange-600",
      "bg-gradient-to-r from-pink-500 to-pink-600"
    ];
    
    return sortedCities.map(([key, count], idx) => ({
      filterKey: key,
      name: key === 'unspecified' ? p('unspecified') : getSaudiWorkCityLabel(key),
      count,
      color: colors[idx]
    }));
  };

  const zoneDistribution = {
    all_zones: {
      title: p('dist_overview_title'),
      data: [
        { filterKey: 'active', name: p('dist_active'), count: stats.active, color: "bg-gradient-to-r from-green-500 to-green-600" },
        { filterKey: 'inactive', name: p('dist_inactive'), count: zones.filter(z => z.status === 'Inactif').length, color: "bg-gradient-to-r from-gray-500 to-gray-600" },
        { filterKey: 'under_construction', name: p('dist_under_construction'), count: zones.filter(z => z.status === 'En Construction').length, color: "bg-gradient-to-r from-orange-500 to-orange-600" },
        { filterKey: 'total_employees', name: p('dist_total_employees'), count: stats.totalEmployees, color: "bg-gradient-to-r from-blue-500 to-blue-600" }
      ]
    },
    par_type: {
      title: p('dist_by_type_title'),
      data: getTypeDistribution()
    },
    par_ville: {
      title: p('dist_by_city_title'),
      data: getCityDistribution()
    }
  };

  const distributionInfo = zoneDistribution[mainViewMode] || zoneDistribution.all_zones;

  
  const baseFilteredZones = useMemo(() => zones.filter(z => {
    if (statusFilter !== 'all' && z.status !== statusFilter) return false;
    
    if (dateRange.start || dateRange.end) {
      const openingDate = new Date(z.opening_date);
      
      if (dateRange.start) {
        const startOfDay = new Date(dateRange.start);
        startOfDay.setHours(0, 0, 0, 0);
        if (openingDate < startOfDay) return false;
      }
      
      if (dateRange.end) {
        const endOfDay = new Date(dateRange.end);
        endOfDay.setHours(23, 59, 59, 999);
        if (openingDate > endOfDay) return false;
      }
    }
    
    if (searchTerm && !z.zone_code?.toLowerCase().includes(searchTerm.toLowerCase()) && !z.zone_name?.toLowerCase().includes(searchTerm.toLowerCase()) && !z.city?.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    if (mainViewMode === 'all_zones') {
      if (quickFilter !== 'all' && normalizeZoneType(z.zone_type) !== quickFilter) return false;
    }
    if (activeDistributionFilter) {
      switch(mainViewMode) {
        case 'all_zones':
          if (activeDistributionFilter === 'active' && z.status !== 'Actif') return false;
          if (activeDistributionFilter === 'inactive' && z.status !== 'Inactif') return false;
          if (activeDistributionFilter === 'under_construction' && z.status !== 'En Construction') return false;
          if (activeDistributionFilter === 'total_employees') return true;
          break;
        case 'par_type':
          if (normalizeZoneType(z.zone_type) !== activeDistributionFilter) return false;
          break;
        case 'par_ville':
          if (activeDistributionFilter !== (z.city || 'unspecified')) return false;
          break;
      }
    }
    return true;
  }), [zones, statusFilter, dateRange, searchTerm, mainViewMode, quickFilter, activeDistributionFilter]);

  const columnFilterRules = useMemo(() => [
      textColumnRule('zone_code', (z) => z.zone_code || ''),
      textColumnRule('zone_name', (z) => z.zone_name || ''),
      textColumnRule('zone_type', (z) => getZoneTypeLabel(z.zone_type)),
      textColumnRule('city', (z) => getSaudiWorkCityLabel(z.city) || z.city || ''),
      textColumnRule('capacity_employees', (z) => String(z.capacity_employees ?? '')),
      textColumnRule('current_employees', (z) => String(z.current_employees ?? '')),
      textColumnRule('manager_name', (z) => z.manager_name || ''),
      exactColumnRule('status', (z) => z.status || ''),
    ], []);

  const filteredZones = useMemo(
    () => applyColumnFilters(baseFilteredZones, colFilters, columnFilterRules),
    [baseFilteredZones, colFilters, columnFilterRules],
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = { ...formData };
      if (editingRecord) {
        await localApiAdapter.put(`/pointage/zones-travail/${editingRecord.id}`, submitData);
        notifyUpdated(showNotify, p('title'));
      } else {
        await localApiAdapter.post('/pointage/zones-travail', submitData);
        notifyCreated(showNotify, p('title'));
      }
    } catch (error) {
      console.error('Error saving:', error);
      notifyError(showNotify, p('save_error')); return;
    } finally {
      resetForm();
      loadZones();
    }
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    setFormData({ ...record });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm(p('delete_confirm'))) {
      try {
        await localApiAdapter.delete(`/pointage/zones-travail/${id}`);
        notifyDeleted(showNotify, p('title'));
        loadZones();
      } catch (error) {
        console.error('Error:', error);
      }
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await localApiAdapter.put(`/pointage/zones-travail/${id}`, { status: newStatus });
      loadZones();
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const handleBulkDelete = async () => {
    if (window.confirm(p('delete_bulk_confirm', { count: selectedRecords.length }))) {
      try {
        const deleteCount = selectedRecords.length;
        await Promise.all(selectedRecords.map(id => localApiAdapter.delete(`/pointage/zones-travail/${id}`)));
        notifyBulkDeleted(showNotify, deleteCount, p('title'));
        setSelectedRecords([]);
        loadZones();
      } catch (error) {
        console.error('Error:', error);
      }
    }
  };

  const resetForm = () => {
    setFormData({ 
      zone_code: '', zone_name: '', zone_type: '', address: '', city: '', postal_code: '',
      capacity_employees: '', current_employees: '', manager_name: '', has_parking: false, 
      has_cafeteria: false, status: '', opening_date: '', notes: ''
    });
    setEditingRecord(null);
    setFormTab('general');
    setShowModal(false);
  };

  const {
    selectedRecords,
    setSelectedRecords,
    allRowsSelected,
    someRowsSelected,
    handleSelectAll,
    handleSelectRecord,
  } = useQawaemFilteredRowSelection(filteredZones);


  const POINTAGE_TABLE_COLUMNS = [
    { key: 'zone_code', labelKey: 'col_code' },
    { key: 'zone_name', labelKey: 'col_name' },
    { key: 'zone_type', labelKey: 'col_type' },
    { key: 'city', labelKey: 'col_city' },
    { key: 'capacity_employees', labelKey: 'col_capacity' },
    { key: 'current_employees', labelKey: 'col_employees' },
    { key: 'manager_name', labelKey: 'col_manager' },
    { key: 'status', labelKey: 'col_status' },
    { key: 'actions', labelKey: 'actions', required: true },
  ];
  const [columnVisibility, setColumnVisibility] = useState(() =>
    buildPointageColumnVisibilityState(POINTAGE_TABLE_COLUMNS),
  );
  const qawaemColumnDefs = useMemo(
    () => buildPointageQawaemColumnDefs(POINTAGE_TABLE_COLUMNS, p, columnVisibility),
    [p, columnVisibility],
  );
  const handleToggleColumn = createPointageHandleToggleColumn(setColumnVisibility, POINTAGE_TABLE_COLUMNS);
  const qawaemVisibleColumnCount = qawaemColumnDefs.filter((c) => c.checked !== false).length;
  const isColVisible = (key) => isPointageColumnVisible(columnVisibility, key);
  const tableColSpan = pointageTableColSpan(columnVisibility);

  return (
    <div className={`space-y-3 employees-container`}>
      <div className="flex justify-between items-center list-toolbar-mobile-layout">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <MapPin className="h-8 w-8 text-blue-400" />
            {p('title')}
            <button onClick={loadZones} className="refresh-button"><RefreshCcw size={20} /></button>
          </h1>
          <p className="text-white/60 mt-2">{p('subtitle')}</p>
        </div>
        <div className="flex gap-3">
          {buildPointageBulkDeleteFilterAction({ count: selectedRecords.length, onDelete: handleBulkDelete, label: `${t('common.delete')} (${selectedRecords.length})`, title: t('common.delete', { defaultValue: 'حذف' }) })}
          <button onClick={() => setShowAdvancedOptions(!showAdvancedOptions)} className={`${showAdvancedOptions ? 'bg-gradient-to-r from-blue-500 to-blue-700 border-blue-400 shadow-lg shadow-blue-500/30' : 'bg-white/5 border-white/20'} border hover:bg-white/10 text-white px-4 py-3 rounded-xl font-semibold hover:border-white/30 transition-all duration-300 flex items-center space-x-2`}>
            <Filter className="h-5 w-5" /><span className="hidden sm:inline">{p('advanced_options')}</span>{showAdvancedOptions ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
          <button className="bg-gradient-to-r from-blue-500 to-blue-700 text-white px-6 py-3 rounded-xl font-semibold hover:from-blue-600 hover:to-blue-800 transition-all duration-300 flex items-center space-x-2" onClick={() => { setEditingRecord(null); setShowModal(true); }}>
            <Plus className="h-5 w-5" /><span>{p('new_zone')}</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {[
          { title: p('stat_total'), value: stats.total, icon: MapPin, color: 'from-blue-500 to-blue-600' },
          { title: p('stat_active'), value: stats.active, icon: CheckCircle, color: 'from-green-500 to-green-600' },
          { title: p('stat_offices'), value: stats.bureaux, icon: Briefcase, color: 'from-purple-500 to-purple-600' },
          { title: p('stat_factories'), value: stats.usines, icon: Building, color: 'from-orange-500 to-orange-600' },
          { title: p('stat_employees'), value: stats.totalEmployees, icon: Users, color: 'from-pink-500 to-pink-600' }
        ].slice(0, 4).map((stat, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }} className="relative group">
            <div className={`absolute -inset-0.5 bg-gradient-to-r ${stat.color} rounded-3xl opacity-0 group-hover:opacity-50 transition-opacity duration-500 blur-sm`}></div>
            <div className="relative bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
              <div className="flex items-center justify-between">
                <div><p className="text-white/60 text-sm font-medium">{stat.title}</p><p className="text-2xl font-bold text-white force-white mt-2">{stat.value}</p></div>
                <div className="relative">
                  <div className={`absolute inset-0 bg-gradient-to-br ${stat.color} rounded-2xl opacity-0 group-hover:opacity-20 transition-opacity duration-300 blur-lg`}></div>
                  <div className={`relative p-3 rounded-2xl bg-gradient-to-r ${stat.color}`}><stat.icon className="h-6 w-6 text-white force-white" /></div>
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      <motion.div initial={false} animate={{ height: showAdvancedOptions ? "auto" : 0, opacity: showAdvancedOptions ? 1 : 0 }} transition={{ duration: 0.3, ease: "easeInOut" }} style={{ overflow: "hidden" }}>
        <div className="space-y-4 pt-4">
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
            <div className="flex space-x-1 overflow-x-auto table-scroll">
              {mainTabs.map((tab) => (
                <button key={tab.id} onClick={() => { setMainViewMode(tab.id); setQuickFilter('all'); setActiveDistributionFilter(null); }} className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${mainViewMode === tab.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}>
                  <tab.icon className="h-5 w-5" /><span className="hidden sm:inline whitespace-nowrap">{tab.name}</span>
                </button>
              ))}
            </div>
          </div>
          {mainViewMode === 'all_zones' && (
            <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
              <div className="flex space-x-1 overflow-x-auto table-scroll">
                {quickFilterButtons.map((filter) => (
                  <button key={filter.id} onClick={() => setQuickFilter(filter.id)} className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${quickFilter === filter.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}>
                    <filter.icon className="h-5 w-5" /><span className="hidden sm:inline whitespace-nowrap">{filter.name}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-white">{distributionInfo.title}</h3>
              {activeDistributionFilter && (
                <button onClick={() => setActiveDistributionFilter(null)} className="text-xs text-white/60 hover:text-white bg-white/5 hover:bg-white/10 px-3 py-1.5 rounded-lg transition-all flex items-center gap-1">
                  <XCircle size={14} />{p('reset_filter')}
                </button>
              )}
            </div>
            <div {...getErpOverviewGridAttrs(distributionInfo.data.length, '4')}>{distributionInfo.data.map((item) => (
                <button key={item.filterKey || item.name} onClick={() => setActiveDistributionFilter(activeDistributionFilter === (item.filterKey || item.name) ? null : (item.filterKey || item.name))} className={`bg-white/5 rounded-xl p-4 border transition-all cursor-pointer text-left ${activeDistributionFilter === (item.filterKey || item.name) ? 'border-blue-400/60 bg-blue-500/20 shadow-lg shadow-blue-500/20' : 'border-white/10 hover:border-white/20 hover:bg-white/10'}`}>
                  <div className="flex justify-between items-center mb-2">
                    <h4 className={`font-medium ${activeDistributionFilter === (item.filterKey || item.name) ? 'text-white' : 'text-white/80'}`}>{item.name}</h4>
                    <div className={`text-sm rounded-full px-2 py-0.5 ${item.color} text-white force-white font-semibold`}>{item.count}</div>
                  </div>
                  <div className="w-full bg-white/10 h-3 rounded-full overflow-hidden">
                    <div className={`h-3 rounded-full ${item.color} transition-all`} style={{ width: `${(() => {
                      const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
                      return total > 0 ? (item.count / total * 100).toFixed(1) : 0;
                    })()}%` }}></div>
                  </div>
                  <p className={`mt-2 text-sm ${activeDistributionFilter === (item.filterKey || item.name) ? 'text-white/80' : 'text-white/60'}`}>{p('pct_of_total', { pct: (() => {
                    const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
                    return total > 0 ? (item.count / total * 100).toFixed(1) : 0;
                  })() })}</p>
                </button>
              ))}
            </div>
          </motion.div>
        </div>
      </motion.div>

      <div className="glass-panel glass-table-shell overflow-visible">
        {viewMode === 'table' && (
          <div className="glass-table-scroll overflow-x-auto">
            <table className="w-full employees-table data-table">
              <thead className="glass-table-head">
                <tr>
                  <QawaemTableSelectAllHeader allRowsSelected={allRowsSelected} someRowsSelected={someRowsSelected} onChange={handleSelectAll} selectAllTitle={t('common.select_all', { defaultValue: 'تحديد الكل' })} deselectAllTitle={t('common.deselect_all', { defaultValue: 'إلغاء تحديد الكل' })} />
                  {isColVisible('zone_code') && (
                    <TableColumnHeaderFilter columnKey="zone_code" label={p('col_code')} value={colFilters.zone_code ?? ''} onChange={(v) => setColFilter('zone_code', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('zone_name') && (
                    <TableColumnHeaderFilter columnKey="zone_name" label={p('col_name')} value={colFilters.zone_name ?? ''} onChange={(v) => setColFilter('zone_name', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('zone_type') && (
                    <TableColumnHeaderFilter columnKey="zone_type" label={p('col_type')} value={colFilters.zone_type ?? ''} onChange={(v) => setColFilter('zone_type', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('city') && (
                    <TableColumnHeaderFilter columnKey="city" label={p('col_city')} value={colFilters.city ?? ''} onChange={(v) => setColFilter('city', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('capacity_employees') && (
                    <TableColumnHeaderFilter columnKey="capacity_employees" label={p('col_capacity')} value={colFilters.capacity_employees ?? ''} onChange={(v) => setColFilter('capacity_employees', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} align="center" thClassName="text-white" />
                  )}
                  {isColVisible('current_employees') && (
                    <TableColumnHeaderFilter columnKey="current_employees" label={p('col_employees')} value={colFilters.current_employees ?? ''} onChange={(v) => setColFilter('current_employees', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} align="center" thClassName="text-white" />
                  )}
                  {isColVisible('manager_name') && (
                    <TableColumnHeaderFilter columnKey="manager_name" label={p('col_manager')} value={colFilters.manager_name ?? ''} onChange={(v) => setColFilter('manager_name', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('status') && (
                    <TableColumnHeaderFilter columnKey="status" label={p('col_status')} kind="select" options={statusColumnFilterOptions} value={colFilters.status ?? ''} onChange={(v) => setColFilter('status', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} align="center" thClassName="text-white" />
                  )}
                  <QawaemTableHeaderActionsCell
                    label={p('actions')}
                    columns={qawaemColumnDefs}
                    onToggle={handleToggleColumn}
                    visibleCount={qawaemVisibleColumnCount}
                    totalCount={qawaemColumnDefs.length}
                  />
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10 employees-table-body">
                {filteredZones.length > 0 ? (
                  filteredZones.map((record, index) => (
                    <tr key={record.id} className={`hover:bg-white/5 transition-colors ${selectedRecords.includes(record.id) ? 'bg-white/10' : ''}`}>
                      <QawaemTableSelectRowCell checked={selectedRecords.includes(record.id)} onChange={() => handleSelectRecord(record.id)} />
                      {isColVisible('zone_code') && (
                        <td className="px-6 py-4"><span className="text-white font-semibold">{record.zone_code}</span></td>
                      )}
                      {isColVisible('zone_name') && (
                        <td className="px-6 py-4"><span className="text-white font-semibold">{record.zone_name}</span></td>
                      )}
                      {isColVisible('zone_type') && (
                        <td className="px-6 py-4"><span className="text-white/70 text-sm">{getZoneTypeLabel(record.zone_type)}</span></td>
                      )}
                      {isColVisible('city') && (
                        <td className="px-6 py-4"><span className="text-white/70 text-sm">{getCityDisplay(record.city) || '-'}</span></td>
                      )}
                      {isColVisible('capacity_employees') && (
                        <td className="px-6 py-4 text-center"><span className="text-white font-bold text-lg">{record.capacity_employees || 0}</span></td>
                      )}
                      {isColVisible('current_employees') && (
                        <td className="px-6 py-4 text-center"><span className="text-white font-bold text-lg">{record.current_employees || 0}</span></td>
                      )}
                      {isColVisible('manager_name') && (
                        <td className="px-6 py-4"><span className="text-white/70 text-sm">{record.manager_name || '-'}</span></td>
                      )}
                      {isColVisible('status') && (
                        <td className="px-6 py-4 text-center">
                          <div className="flex justify-center">
                            <QawaemClickableStatusBadge
                              status={record.status}
                              recordId={record.id}
                              options={statusOptions}
                              onChange={handleStatusChange}
                              title={p('click_change_status', { defaultValue: t('common.change_status') })}
                            />
                          </div>
                        </td>
                      )}
                      <td className="px-6 py-4 text-center align-middle">
                        <div className="flex justify-center">
                          <QawaemErpRowActions
                          onView={() => { setViewingRecord(record); setShowViewModal(true); }}
                          onEdit={() => handleEdit(record)}
                          onDelete={() => handleDelete(record.id)}
                          viewTitle={t('common.view')}
                          editTitle={t('common.edit')}
                          deleteTitle={t('common.delete')}
                          />
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr><td colSpan={tableColSpan} className="text-center py-12 text-white/60"><MapPin size={48} className="mx-auto mb-4 text-white/30" /><p className="text-lg">{p('empty')}</p></td></tr>
                )}
              </tbody>
              <tfoot>
                <tr className="bg-white/5 border-t border-white/10">
                  <td colSpan={tableColSpan} className="px-6 py-4">
                    <div className="flex items-center justify-between text-white/70 text-sm">
                      <span>{p('footer_total', { count: filteredZones.length })}</span>
                      <span>{p('footer_capacity')} <span className="text-white font-semibold">{filteredZones.reduce((sum, z) => sum + (parseInt(z.capacity_employees) || 0), 0)}</span></span>
                      <span>{p('footer_current')} <span className="text-white font-semibold">{filteredZones.reduce((sum, z) => sum + (parseInt(z.current_employees) || 0), 0)}</span></span>
                    </div>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}

        {viewMode === 'cards' && (
          <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            {filteredZones.length > 0 ? (
              filteredZones.map((record, index) => (
                <motion.div key={record.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }} className="bg-white/[0.03] rounded-2xl p-6 border border-white/20 hover:border-white/40 hover:bg-white/[0.05] transition-all h-full min-w-0">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-4 mb-4">
                        <h3 className="text-xl font-bold text-white flex items-center gap-2"><MapPin size={20} className="text-emerald-400" />{record.zone_name}</h3>
                        <div className="flex">{getStatusBadge(record.status)}</div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_section_zone')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_code')}</span> {record.zone_code}</div>
                            <div><span className="font-medium">{p('label_name')}</span> {record.zone_name}</div>
                            <div><span className="font-medium">{p('label_type')}</span> {getZoneTypeLabel(record.zone_type)}</div>
                          </div>
                        </div>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_section_location')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('col_city')}:</span> {getCityDisplay(record.city)}</div>
                            <div><span className="font-medium">{p('label_address')}</span> {record.address || p('em_dash')}</div>
                            <div><span className="font-medium">{p('label_postal')}</span> {record.postal_code || p('em_dash')}</div>
                          </div>
                        </div>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_section_capacity')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('col_capacity')}:</span> <span className="text-white font-bold text-lg">{record.capacity_employees || 0}</span></div>
                            <div><span className="font-medium">{p('label_current_employees')}</span> <span className="text-white font-bold text-lg">{record.current_employees || 0}</span></div>
                            <div className="flex gap-2 mt-2">
                              {record.has_parking && <span className="px-2 py-1 bg-blue-500/20 text-blue-300 rounded text-xs flex items-center gap-1"><Car size={12} />{p('parking')}</span>}
                              {record.has_cafeteria && <span className="px-2 py-1 bg-green-500/20 text-green-300 rounded text-xs flex items-center gap-1"><Coffee size={12} />{p('cafeteria')}</span>}
                            </div>
                          </div>
                        </div>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_section_manager')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_name')}</span> {record.manager_name || p('em_dash')}</div>
                            {record.opening_date && (
                              <div><span className="font-medium">{p('label_opening_date')}</span> {formatPointageDate(record.opening_date)}</div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-1 ml-4 shrink-0">
                      <QawaemErpRowActions
                        compact
                        size={14}
                        onView={() => { setViewingRecord(record); setShowViewModal(true); }}
                        onEdit={() => handleEdit(record)}
                        onDelete={() => handleDelete(record.id)}
                        viewTitle={t('common.view')}
                        editTitle={t('common.edit')}
                        deleteTitle={t('common.delete')}
                      />
                    </div>
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="col-span-full text-center py-12"><MapPin size={48} className="mx-auto mb-4 text-white/30" /><p className="text-white/60 text-lg">{p('empty')}</p></div>
            )}
          </div>
        )}
      </div>

      {showViewModal && viewingRecord && (
        <QawaemErpViewModal
          open={showViewModal}
          onClose={() => setShowViewModal(false)}
          title={p('view_modal_title')}
          icon={Eye}
          maxWidth="1100px"
          testId="zones-travail-view-modal"
        >
          <div className="space-y-4">
                <div className={modalForm.viewCard}>
                  <h3 className={`${modalForm.viewHeading} flex items-center gap-2`}><MapPin size={18} className="text-emerald-400" />{p('view_section_zone')}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div><p className={modalForm.viewLabel}>{p('view_zone_code')}</p><p className={modalForm.viewValue}>{viewingRecord.zone_code}</p></div>
                    <div><p className={modalForm.viewLabel}>{p('col_name')}</p><p className={modalForm.viewValue}>{viewingRecord.zone_name}</p></div>
                    <div><p className={modalForm.viewLabel}>{p('type')}</p><p className={modalForm.viewValue}>{getZoneTypeLabel(viewingRecord.zone_type)}</p></div>
                    <div><p className={modalForm.viewLabel}>{p('status')}</p>{getStatusBadge(viewingRecord.status)}</div>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className={modalForm.viewCard}>
                    <h3 className={modalForm.viewHeading}>{p('view_section_location')}</h3>
                    <div className="space-y-3">
                      <div><p className={modalForm.viewLabel}>{p('col_city')}</p><p className={modalForm.viewValue}>{viewingRecord.city || p('em_dash')}</p></div>
                      <div><p className={modalForm.viewLabel}>{p('view_address')}</p><p className={modalForm.viewValue}>{viewingRecord.address || p('em_dash')}</p></div>
                      <div><p className={modalForm.viewLabel}>{p('view_postal')}</p><p className={modalForm.viewValue}>{viewingRecord.postal_code || p('em_dash')}</p></div>
                    </div>
                  </div>
                  <div className={modalForm.viewCard}>
                    <h3 className={modalForm.viewHeading}>{p('view_section_capacity')}</h3>
                    <div className="space-y-3">
                      <div><p className={modalForm.viewLabel}>{p('view_capacity')}</p><p className={modalForm.viewValue}>{viewingRecord.capacity_employees || 0}</p></div>
                      <div><p className={modalForm.viewLabel}>{p('view_current')}</p><p className={modalForm.viewValue}>{viewingRecord.current_employees || 0}</p></div>
                      <div className="flex gap-2">
                        {viewingRecord.has_parking && <span className="px-3 py-1 bg-blue-500/20 text-blue-300 rounded-full text-xs flex items-center gap-1"><Car size={14} />{p('parking')}</span>}
                        {viewingRecord.has_cafeteria && <span className="px-3 py-1 bg-green-500/20 text-green-300 rounded-full text-xs flex items-center gap-1"><Coffee size={14} />{p('cafeteria')}</span>}
                      </div>
                    </div>
                  </div>
                </div>
                <div className={modalForm.viewCard}>
                  <h3 className={modalForm.viewHeading}>{p('view_section_manager')}</h3>
                  <div className="space-y-3">
                    <div><p className={modalForm.viewLabel}>{p('view_manager')}</p><p className={modalForm.viewValue}>{viewingRecord.manager_name || p('em_dash')}</p></div>
                    {viewingRecord.opening_date && (
                      <div><p className={modalForm.viewLabel}>{p('view_opening_date')}</p><p className={modalForm.viewValue}>{formatPointageDate(viewingRecord.opening_date)}</p></div>
                    )}
                    {viewingRecord.notes && (
                      <div><p className={modalForm.viewLabel}>{p('field_notes')}</p><p className="text-white/80 text-sm">{viewingRecord.notes}</p></div>
                    )}
                  </div>
                </div>
              </div>
        </QawaemErpViewModal>
      )}

      {showModal && (
        <QawaemErpCrudModal
          open={showModal}
          onClose={resetForm}
          title={editingRecord ? p('modal_edit') : p('modal_new')}
          icon={MapPin}
          onSubmit
          formId="zone-form"
          saveLabel={editingRecord ? p('update') : p('create')}
          maxWidth="1200px"
          testId="zones-travail-crud-modal"
        >
          <PointageFormModalShell
            isLight={cfgTheme.light}
            tabs={zoneFormTabs}
            activeTab={formTab}
            onTabChange={setFormTab}
            formId="zone-form"
            onSubmit={handleSubmit}
            ariaLabel={editingRecord ? p('modal_edit') : p('modal_new')}
          >
            {pointageFormTabVisible(zoneFormTabs, formTab, 'general') && (
              <div className="space-y-4">
              <div className={POINTAGE_FORM_GRID_CLASS}>
                <div><label className={modalForm.label}>{<RequiredLabelText text={p('field_zone_code')} />}</label><input type="text" required value={formData.zone_code} onChange={(e) => setFormData({ ...formData, zone_code: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('ph_zone_code')} /></div>
                <div><label className={modalForm.label}>{<RequiredLabelText text={p('field_zone_name')} />}</label><input type="text" required value={formData.zone_name} onChange={(e) => setFormData({ ...formData, zone_name: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('ph_zone_name')} /></div>
                <div><label className={modalForm.label}>{p('field_zone_type')}</label><CustomSelect value={formData.zone_type} onChange={(value) => setFormData({ ...formData, zone_type: value })} options={zoneTypeOptions} searchable={true} creatable={true} autoSelect={false} /></div>
                <div><label className={modalForm.label}>{p('field_status')}</label><CustomSelect value={formData.status} onChange={(value) => setFormData({ ...formData, status: value })} options={statusOptions.map(s => ({ value: s.value, label: s.label, icon: <s.icon size={14} className={s.style.split(' ')[1]} /> }))} searchable={true} creatable={true} autoSelect={false} /></div>
              </div>
              <div className={POINTAGE_FORM_GRID_CLASS}>
                <div><label className={modalForm.label}>{p('field_address')}</label><input type="text" value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('ph_address')} /></div>
                <div><label className={modalForm.label}>{p('field_city')}</label><CustomSelect value={formData.city} onChange={(value) => setFormData({ ...formData, city: value })} options={cityOptions} searchable={true} creatable={true} autoSelect={false} placeholder={p('ph_city')} /></div>
                <div><label className={modalForm.label}>{p('field_postal')}</label><input type="text" value={formData.postal_code} onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('ph_postal')} /></div>
              </div>
              <div className={POINTAGE_FORM_GRID_CLASS}>
                <div><label className={modalForm.label}>{p('field_opening_date')}</label><CustomDatePicker value={formData.opening_date} onChange={(value) => setFormData({ ...formData, opening_date: value })} placeholder={p('select')} /></div>
              </div>
              </div>
            )}
            {pointageFormTabVisible(zoneFormTabs, formTab, 'details') && (
              <div className="space-y-4">
              <div className={POINTAGE_FORM_GRID_CLASS}>
                <div><label className={modalForm.label}>{p('field_capacity')}</label><CustomNumberInput step={1} value={formData.capacity_employees || ''} onChange={(value) => setFormData({ ...formData, capacity_employees: value })} placeholder="0" /></div>
                <div><label className={modalForm.label}>{p('field_current')}</label><CustomNumberInput step={1} value={formData.current_employees || ''} onChange={(value) => setFormData({ ...formData, current_employees: value })} placeholder="0" /></div>
                <div className="flex items-center gap-2">
                  <input type="checkbox" id="has_parking" checked={formData.has_parking} onChange={(e) => setFormData({ ...formData, has_parking: e.target.checked })} className="w-4 h-4 rounded border-white/20 bg-white/10 text-blue-500 focus:ring-2 focus:ring-blue-500" />
                  <label htmlFor="has_parking" className="text-white/70 text-sm font-medium flex items-center gap-2"><Car size={16} />{p('parking_available')}</label>
                </div>
                <div className="flex items-center gap-2">
                  <input type="checkbox" id="has_cafeteria" checked={formData.has_cafeteria} onChange={(e) => setFormData({ ...formData, has_cafeteria: e.target.checked })} className="w-4 h-4 rounded border-white/20 bg-white/10 text-blue-500 focus:ring-2 focus:ring-blue-500" />
                  <label htmlFor="has_cafeteria" className="text-white/70 text-sm font-medium flex items-center gap-2"><Coffee size={16} />{p('cafeteria_available')}</label>
                </div>
              </div>
              <div className={POINTAGE_FORM_GRID_CLASS}>
                <div><label className={modalForm.label}>{p('field_manager')}</label><input type="text" value={formData.manager_name} onChange={(e) => setFormData({ ...formData, manager_name: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('ph_manager')} /></div>
                <div className="md:col-span-3"><label className={modalForm.label}>{p('field_notes')}</label><textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows={3} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('ph_notes')}></textarea></div>
              </div>
              </div>
            )}
          </PointageFormModalShell>
        </QawaemErpCrudModal>
      )}
      <PointageCrudNotificationToast toast={notifyToast} onDismiss={dismissNotify} />
    </div>
  );
};

export default ZonesTravailPage;

